import type { Result } from 'true-myth'

import { VALUE_SCHEMAS } from '@/lib/facts/values'
import type { Locale } from '@/i18n/routing'

import type { ValueFormatters } from './format'
import {
    attempt,
    formatIcu,
    formatValue,
    sentinelIndex,
    type MessageFailure,
    type Variable,
} from './icu'
import { sentenceCased, splicePlain } from './sentence'
import { withServedSpacing } from './typography'
import type { StyleKind } from './variables'

/*
 * A message a client island formats against a value only the client knows — a selected
 * passenger count, a fetched trip's fare. The server hands the island the message's source
 * in one locale; the island renders it on each change through the ICU half of the grammar
 * the server renders by, and a template is never markdown: a client island parses none.
 * Where the server holds the values too, it renders the same template and hands the island
 * the text.
 */

/** The kinds a client template's values take: every value kind but an href, which means
 *  something only as a link target, and a link is markdown. */
export type TemplateKind = Exclude<StyleKind, 'href'>

export const isTemplateKind = (kind: StyleKind): kind is TemplateKind => kind !== 'href'

/** A client template's values, each read as the kind its registry entry gives it. */
export type TemplateValues = Readonly<Record<string, unknown>>

declare const VALUES: unique symbol

/** One locale's source of a store message, where it came from, so a failure names the
 *  file and key, and the kind of each value the island supplies — its copy set's registry
 *  entry's, or its reader's declaration's. It carries no format: how a value is stated is
 *  the page's decision, which the island binds. */
export interface MessageTemplate<Values extends TemplateValues = TemplateValues> {
    readonly locale: Locale
    readonly source: string
    readonly file: string
    readonly key: string
    readonly kinds: Readonly<Record<string, TemplateKind>>
    /** Every other name the message uses that the server could resolve, already typed. A
     *  bound value stays a value, never text spliced into the source, so a plural or a
     *  select still chooses its branch on it. */
    readonly bound?: Readonly<Record<string, Variable>>
    /** Type-only: the values the island supplies, which no template carries at runtime.
     *  Read both ways, so a template of one set of values never passes for another's. */
    readonly [VALUES]?: (values: Values) => Values
}

/** A value of the wrong kind is the caller's defect, not the copy's, so it throws. */
function typed(kind: TemplateKind, name: string, value: unknown): Variable {
    if (kind === 'text') {
        if (typeof value !== 'string') {
            throw new TypeError(`{${name}} is text, not ${typeof value}`)
        }
        return { kind, value }
    }
    return { kind, value: VALUE_SCHEMAS[kind].parse(value) } as Variable
}

/** The template's text for the values, each formatted by its kind in the presentation its
 *  style names, by the formatters of the page's decision. */
export function renderTemplate<Values extends TemplateValues>(
    { locale, source, kinds, bound = {} }: MessageTemplate<Values>,
    values: Values,
    format: ValueFormatters,
): Result<string, MessageFailure> {
    // A value the caller holds none of is absent, as it is to a copy set's template.
    const variables: Readonly<Record<string, Variable>> = {
        ...bound,
        ...Object.fromEntries(
            Object.entries(values).flatMap(([name, value]) =>
                Object.hasOwn(kinds, name) && value !== undefined && value !== null
                    ? [[name, typed(kinds[name], name, value)]]
                    : [],
            ),
        ),
    }
    return attempt(() => {
        const { output, slots } = formatIcu(source, {
            locale,
            lookup: (name) =>
                Object.hasOwn(variables, name)
                    ? { status: 'present', variable: variables[name] }
                    : { status: 'absent' },
        })
        return splicePlain(withServedSpacing(output, locale), (char, startsSentence) => {
            const { variable, presentation } = slots[sentinelIndex(char)]
            // Every sentinel left after the missing-variable check stands for a value.
            const formatted = formatValue(variable!, format, presentation)
            return sentenceCased(variable!, formatted, startsSentence, locale)
        })
    })
}

/** What a template that cannot render does, where it renders: outside production it throws,
 *  so a test or a dev render fails on it; in production it logs an error naming the file and
 *  key, and renders empty text.
 *  @internal For the two renderers, `useClientTemplate`'s and `serverTemplate`'s; callers
 *  render a template through one of them. */
export interface FailurePolicy {
    readonly production: boolean
    readonly log: (message: string, context: Record<string, unknown>) => void
}

/** The template's text for the values, by the formatters of the page's decision; a template
 *  that cannot render is handled by `policy`.
 *  @internal For the two renderers, `useClientTemplate`'s and `serverTemplate`'s; callers
 *  render a template through one of them. */
export function renderTemplateText<Values extends TemplateValues>(
    template: MessageTemplate<Values>,
    values: Values,
    format: ValueFormatters,
    { production, log }: FailurePolicy,
): string {
    return renderTemplate(template, values, format).match({
        Ok: (text) => text,
        Err: (failure) => {
            const missing = failure.cause === 'missing-variable' ? [...failure.missing] : undefined
            if (!production) {
                throw new Error(
                    `${template.file} "${template.key}" is unrenderable (${failure.cause}${
                        missing ? `: ${missing.join(', ')}` : ''
                    })`,
                )
            }
            log('Client template is unrenderable', {
                file: template.file,
                key: template.key,
                cause: failure.cause,
                ...(missing ? { missing } : {}),
            })
            return ''
        },
    })
}
